import { useState } from 'react';
import {
    Filter,
    X,
    Search,
    ChevronDown,
    Tag,
    Save,
    Plus,
    Circle,
    Flag,
    User as UserIcon,
    Calendar,
    Hash,
    Type as TypeIcon,
    ListFilter,
    ListChecks,
    CheckSquare,
    Link as LinkIcon,
    Mail,
    Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { TaskStatus, TaskPriority, CustomField, ViewConfig } from '@/lib/schemas';
import { statusConfig, priorityConfig, getStatusLabel, getPriorityLabel } from './task-table';
import type { TaskFilters } from '@/hooks/use-tasks';
import { useI18n } from '@/lib/i18n';
import { CustomFieldFilterEditor, OPERATORS_BY_TYPE, type CustomFieldFilterValue } from './custom-field-filter-editor';

// ============================================
// t45 (R14): Custom field chip styling helpers
// ============================================
//
// Per-type colour + icon so each custom-field filter chip is
// visually distinguishable. The colour is used as a left-border
// accent; the icon goes before the field name in the chip label.

const FIELD_TYPE_COLOR: Record<CustomField['type'], string> = {
    TEXT: '#60a5fa',          // blue-400
    NUMBER: '#a78bfa',       // violet-400
    DATE: '#f59e0b',         // amber-500
    SELECT: '#34d399',       // emerald-400
    MULTI_SELECT: '#22d3ee', // cyan-400
    CHECKBOX: '#a3e635',     // lime-400
    URL: '#f472b6',          // pink-400
    EMAIL: '#fb923c',        // orange-400
    PERSON: '#c084fc',       // purple-400
    SPRINT: '#facc15',       // yellow-400
};

const FIELD_TYPE_ICON: Record<CustomField['type'], React.ComponentType<{ className?: string }>> = {
    TEXT: TypeIcon,
    NUMBER: Hash,
    DATE: Calendar,
    SELECT: ListFilter,
    MULTI_SELECT: ListChecks,
    CHECKBOX: CheckSquare,
    URL: LinkIcon,
    EMAIL: Mail,
    PERSON: UserIcon,
    SPRINT: Zap,
};

// Group custom fields by type for the "+ Filter" dropdown so users
// can find date fields under "Dates", people under "People", etc.
const FIELD_TYPE_GROUP: Record<CustomField['type'], string> = {
    TEXT: 'text',
    NUMBER: 'number',
    DATE: 'date',
    SELECT: 'category',
    MULTI_SELECT: 'category',
    CHECKBOX: 'category',
    URL: 'text',
    EMAIL: 'text',
    PERSON: 'person',
    SPRINT: 'category',
};

// t47 (R14): narrow the value type to the concrete Translations key
// for filter group labels — `t()` requires `keyof Translations`.
// Without this `as const` the value is widened to `string`, the
// record's lookup returns `string | undefined`, and TS rejects the
// `t(FIELD_TYPE_GROUP_I18N[g])` call below.
const FIELD_TYPE_GROUP_I18N = {
    text: 'filter.group.text',
    number: 'filter.group.number',
    date: 'filter.group.date',
    category: 'filter.group.category',
    person: 'filter.group.person',
} as const satisfies Record<string, string>;
type FieldTypeGroupKey = keyof typeof FIELD_TYPE_GROUP_I18N;

// ============================================
// PROPS
// ============================================

interface TaskFiltersBarProps {
    filters: TaskFilters;
    onFiltersChange: (filters: TaskFilters) => void;
    members: Array<{ userId: string; user: { id: string; name: string; avatarUrl: string | null } }>;
    customFields?: CustomField[];
    onSaveAsView?: (config: ViewConfig) => void;
    onUpdateView?: () => void;
    hasActiveView?: boolean;
    onAddCustomField?: () => void;
}

// ============================================
// COMPONENT
// ============================================

export function TaskFiltersBar({
    filters,
    onFiltersChange,
    members,
    customFields = [],
    onSaveAsView,
    onUpdateView,
    hasActiveView,
    onAddCustomField,
}: TaskFiltersBarProps) {
    const { t } = useI18n();
    const [searchValue, setSearchValue] = useState(filters.search || '');

    // Count filters including custom fields. t43 (R13): the value
    // shape is now `{ type, op, value?, value2? }` for active filters
    // OR `string[]` (legacy). A filter counts as "active" if the
    // object has an op OR the array is non-empty.
    const customFieldFilterCount = Object.values(filters.customFields || {}).filter(
        (raw) => {
            if (Array.isArray(raw)) return raw.length > 0;
            if (raw && typeof raw === 'object') {
                return typeof (raw as { op?: string }).op === 'string';
            }
            return false;
        }
    ).length;

    const activeFilterCount = [
        filters.status?.length ?? 0,
        filters.priority?.length ?? 0,
        filters.assignee ? 1 : 0,
        filters.dueDateFrom || filters.dueDateTo ? 1 : 0,
        customFieldFilterCount,
    ].reduce((a, b) => a + b, 0);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onFiltersChange({ ...filters, search: searchValue || undefined });
    };

    const toggleStatus = (status: TaskStatus) => {
        const current = filters.status || [];
        const updated = current.includes(status)
            ? current.filter((s) => s !== status)
            : [...current, status];
        onFiltersChange({ ...filters, status: updated.length > 0 ? updated : undefined });
    };

    const togglePriority = (priority: TaskPriority) => {
        const current = filters.priority || [];
        const updated = current.includes(priority)
            ? current.filter((p) => p !== priority)
            : [...current, priority];
        onFiltersChange({ ...filters, priority: updated.length > 0 ? updated : undefined });
    };

    const setAssignee = (userId: string | undefined) => {
        onFiltersChange({ ...filters, assignee: userId });
    };

    // t43 (R13): set / clear a per-type custom-field filter. The
    // shape stored in filters.customFields[fieldId] is now
    // `{ type, op, value?, value2? }` (legacy option-id-list is
    // still accepted by the backend for backward compat).
    const setCustomFieldFilter = (
        fieldId: string,
        next: CustomFieldFilterValue | null
    ) => {
        const currentFieldFilters = { ...(filters.customFields || {}) };
        if (next === null) {
            delete currentFieldFilters[fieldId];
        } else {
            currentFieldFilters[fieldId] = next;
        }
        onFiltersChange({
            ...filters,
            customFields: Object.keys(currentFieldFilters).length > 0 ? currentFieldFilters : undefined,
        });
    };

    const clearFilters = () => {
        setSearchValue('');
        onFiltersChange({});
    };

    // t43 (R13): all 10 custom-field types are filterable now.
    // SELECT / MULTI_SELECT / PERSON / SPRINT need their options /
    // members to render a value picker; the others (TEXT / NUMBER /
    // DATE / CHECKBOX / URL / EMAIL) take a free-text / number /
    // date / toggle input. We don't pre-filter the list — render
    // every custom field in the "+ Filter" dropdown.
    const filterableFields = customFields;

    return (
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {/* Search */}
            <form onSubmit={handleSearchSubmit} className="relative w-full sm:flex-1 sm:min-w-[200px] sm:max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
                <Input
                    type="text"
                    placeholder={t('filters.searchTasks')}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    className="pl-9 pr-9"
                />
                {searchValue && (
                    <button
                        type="button"
                        onClick={() => {
                            setSearchValue('');
                            onFiltersChange({ ...filters, search: undefined });
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </form>

            {/* Status Filter */}
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-9 text-xs sm:text-sm whitespace-nowrap"
                    >
                        <Circle className="h-3.5 w-3.5 mr-1.5 text-sky-400 fill-sky-400/30" />
                        {t('filters.status')}
                        {filters.status && filters.status.length > 0 && (
                            <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                                {filters.status.length}
                            </Badge>
                        )}
                        <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48 p-2">
                    {Object.entries(statusConfig).map(([key, config]) => (
                        <label
                            key={key}
                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-hover cursor-pointer"
                        >
                            <Checkbox
                                checked={filters.status?.includes(key as TaskStatus) || false}
                                onCheckedChange={() => toggleStatus(key as TaskStatus)}
                            />
                            <Badge variant={config.variant} className="text-xs">
                                {getStatusLabel(key as TaskStatus, t)}
                            </Badge>
                        </label>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Priority Filter */}
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-9 text-xs sm:text-sm whitespace-nowrap"
                    >
                        <Flag className="h-3.5 w-3.5 mr-1.5 text-rose-400" />
                        {t('filters.priority')}
                        {filters.priority && filters.priority.length > 0 && (
                            <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                                {filters.priority.length}
                            </Badge>
                        )}
                        <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-40 p-2">
                    {Object.entries(priorityConfig).map(([key, config]) => (
                        <label
                            key={key}
                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-hover cursor-pointer"
                        >
                            <Checkbox
                                checked={filters.priority?.includes(key as TaskPriority) || false}
                                onCheckedChange={() => togglePriority(key as TaskPriority)}
                            />
                            <Badge variant={config.variant} className="text-xs">
                                {getPriorityLabel(key as TaskPriority, t)}
                            </Badge>
                        </label>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Assignee Filter */}
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-9 text-xs sm:text-sm whitespace-nowrap"
                    >
                        <UserIcon className="h-3.5 w-3.5 mr-1.5 text-violet-400" />
                        {t('filters.assignee')}
                        {filters.assignee && (
                            <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                                1
                            </Badge>
                        )}
                        <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56 p-2 max-h-60 overflow-y-auto">
                    <label
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-hover cursor-pointer"
                    >
                        <Checkbox
                            checked={!filters.assignee}
                            onCheckedChange={() => setAssignee(undefined)}
                        />
                        <span className="text-sm">{t('filters.allAssignees')}</span>
                    </label>
                    {members.map((member) => (
                        <label
                            key={member.userId}
                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-hover cursor-pointer"
                        >
                            <Checkbox
                                checked={filters.assignee === member.userId}
                                onCheckedChange={() =>
                                    setAssignee(
                                        filters.assignee === member.userId
                                            ? undefined
                                            : member.userId
                                    )
                                }
                            />
                            <span className="text-sm truncate">{member.user.name}</span>
                        </label>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>

            {/* t43 (R13): Active custom-field filters. Each filter row
                shows the operator dropdown + value editor for one
                field. t45 (R14) wraps each in a coloured left-border
                accent + type-specific icon so the chips are easy to
                tell apart at a glance. */}
            {customFields
                .filter((f) => {
                    const raw = filters.customFields?.[f.id];
                    if (Array.isArray(raw)) return raw.length > 0;
                    if (raw && typeof raw === 'object') {
                        return typeof (raw as { op?: string }).op === 'string';
                    }
                    return false;
                })
                .map((field) => {
                    const raw = filters.customFields?.[field.id];
                    // Coerce legacy shape to the new shape for the editor.
                    const current: CustomFieldFilterValue | null =
                        Array.isArray(raw)
                            ? {
                                  type: field.type,
                                  op: field.type === 'MULTI_SELECT' ? 'containsAny' : 'isAnyOf',
                                  value: raw,
                              }
                            : (raw as CustomFieldFilterValue | null);
                    const accentColor = FIELD_TYPE_COLOR[field.type];
                    const FieldIcon = FIELD_TYPE_ICON[field.type];
                    return (
                        <div
                            key={field.id}
                            className="flex items-center gap-1 rounded-md border border-white/10 bg-surface/40 px-1.5 py-1.5"
                        >
                            <span
                                className="flex items-center text-foreground-muted shrink-0"
                                style={{ color: accentColor }}
                                title={field.type}
                            >
                                <FieldIcon className="h-3.5 w-3.5" />
                            </span>
                            <CustomFieldFilterEditor
                                field={field}
                                value={current}
                                onChange={(next) => setCustomFieldFilter(field.id, next)}
                                members={members}
                            />
                        </div>
                    );
                })}

            {/* t43 (R13): "+ Filter" button — opens a dropdown listing
                every custom field (all 10 types) grouped by category.
                t45 (R14): each field gets a per-type icon + coloured
                swatch so the dropdown is scannable. */}
            {filterableFields.length > 0 && (() => {
                // Group fields by their category for the dropdown.
                const grouped: Partial<Record<FieldTypeGroupKey, CustomField[]>> = {};
                for (const field of filterableFields) {
                    const g = FIELD_TYPE_GROUP[field.type] as FieldTypeGroupKey;
                    if (!grouped[g]) grouped[g] = [];
                    grouped[g]!.push(field);
                }
                // Stable group order: Categories → People → Dates → Numbers → Text.
                const groupOrder: FieldTypeGroupKey[] = ['category', 'person', 'date', 'number', 'text'];
                const activeGroups = groupOrder.filter((g) => (grouped[g]?.length ?? 0) > 0);
                return (
                    <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-9 text-xs sm:text-sm whitespace-nowrap border-dashed"
                            >
                                <Plus className="h-3.5 w-3.5 mr-1.5" />
                                {t('filter.addFilter')}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-64 max-h-80 overflow-y-auto p-1">
                            <DropdownMenuLabel className="text-xs uppercase tracking-wider text-foreground-muted px-2 pb-0.5">
                                {t('filter.filterByField')}
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {activeGroups.map((g, groupIdx) => {
                                const labelKey = FIELD_TYPE_GROUP_I18N[g];
                                const fieldsInGroup = grouped[g] ?? [];
                                return (
                                    <div key={g}>
                                        {groupIdx > 0 && <DropdownMenuSeparator />}
                                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-foreground-muted/80 px-2 pt-1.5 pb-0.5">
                                            {t(labelKey)}
                                        </DropdownMenuLabel>
                                        {fieldsInGroup.map((field) => {
                                            const op = (OPERATORS_BY_TYPE[field.type] ?? [])[0] ?? 'isEmpty';
                                            const active =
                                                filters.customFields?.[field.id] !== undefined;
                                            const accentColor = FIELD_TYPE_COLOR[field.type];
                                            const FieldIcon = FIELD_TYPE_ICON[field.type];
                                            return (
                                                <DropdownMenuItem
                                                    key={field.id}
                                                    onClick={() =>
                                                        setCustomFieldFilter(field.id, {
                                                            type: field.type,
                                                            op,
                                                        })
                                                    }
                                                    className="cursor-pointer gap-1.5 py-1.5"
                                                >
                                                    <span
                                                        className="shrink-0"
                                                        style={{ color: accentColor }}
                                                    >
                                                        <FieldIcon className="h-3.5 w-3.5" />
                                                    </span>
                                                    <span className="text-sm truncate flex-1">
                                                        {field.name}
                                                    </span>
                                                    {active && <span className="ml-1 text-primary">✓</span>}
                                                </DropdownMenuItem>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            })()}

            {/* Active filters indicator and clear */}
            {activeFilterCount > 0 && (
                <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="h-7 px-2">
                        <Filter className="h-3 w-3 mr-1" />
                        {activeFilterCount} {t('filters.filter')}{activeFilterCount > 1 ? 's' : ''}
                    </Badge>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                        className="h-7 px-2 text-foreground-muted hover:text-foreground"
                    >
                        {t('filters.clearAll')}
                        <X className="h-3 w-3 ml-1" />
                    </Button>
                </div>
            )}

            {/* Save as View button */}
            {onSaveAsView && activeFilterCount > 0 && !hasActiveView && (
                <Button
                    variant="outline"
                    size="sm"
                    className="h-9 text-xs sm:text-sm whitespace-nowrap"
                    onClick={() => {
                        const config: ViewConfig = {
                            filters: {
                                status: filters.status,
                                priority: filters.priority,
                                assignees: filters.assignee ? [filters.assignee] : undefined,
                                search: filters.search,
                                // t43 (R13): cast through unknown so the
                                // new per-type filter shape flows into
                                // the ViewConfig zod inferred type.
                                customFields: filters.customFields as
                                    | Record<string, string[] | {
                                        type: 'TEXT' | 'NUMBER' | 'DATE' | 'SELECT' | 'MULTI_SELECT' | 'CHECKBOX' | 'URL' | 'EMAIL' | 'PERSON' | 'SPRINT';
                                        op: string;
                                        value?: unknown;
                                        value2?: unknown;
                                    }>
                                    | undefined,
                            },
                            sorts: filters.sortBy ? [{
                                field: filters.sortBy,
                                order: filters.sortOrder || 'asc',
                            }] : undefined,
                        };
                        onSaveAsView(config);
                    }}
                >
                    <Save className="h-3.5 w-3.5 mr-1.5" />
                    {t('filters.saveAsView')}
                </Button>
            )}

            {/* Update View button (shown when active view is modified) */}
            {onUpdateView && hasActiveView && (
                <Button
                    variant="outline"
                    size="sm"
                    className="h-9 text-xs sm:text-sm border-primary text-primary hover:bg-primary/10 whitespace-nowrap"
                    onClick={onUpdateView}
                >
                    <Save className="h-3.5 w-3.5 mr-1.5" />
                    {t('filters.saveViewChanges')}
                </Button>
            )}

            {/* Add Custom Field button */}
            {onAddCustomField && (
                <Button
                    variant="outline"
                    size="sm"
                    className="h-9 text-xs sm:text-sm whitespace-nowrap"
                    onClick={onAddCustomField}
                >
                    <Plus className="h-3.5 w-3.5 mr-1.5" />
                    {t('filters.addField')}
                </Button>
            )}
        </div>
    );
}
